import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = not auth, obj = auth
    const [error, setError] = useState("");

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (_e) {
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data?.requires_2fa) {
                // Don't set user — caller will route to 2FA step
                return { requires_2fa: true, mfa_token: data.mfa_token, email: data.email };
            }
            setUser(data);
            return data;
        } catch (e) {
            const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
            setError(msg);
            throw new Error(msg);
        }
    };

    const verify2fa = async (mfa_token, code) => {
        setError("");
        try {
            const { data } = await api.post("/auth/2fa/verify-login", { mfa_token, code });
            setUser(data);
            return data;
        } catch (e) {
            const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
            setError(msg);
            throw new Error(msg);
        }
    };

    const register = async (payload) => {
        setError("");
        try {
            const { data } = await api.post("/auth/register", payload);
            setUser(data);
            return data;
        } catch (e) {
            const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
            setError(msg);
            throw new Error(msg);
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (_e) {
            // ignore
        }
        setUser(false);
    };

    return (
        <AuthCtx.Provider value={{ user, error, login, register, logout, refresh, setUser, verify2fa }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    return useContext(AuthCtx);
}
